import { Injectable, OnDestroy, inject } from '@angular/core';
import { Experience } from '@ceres/domain';
import { ImpersonatedHttpClient } from '@ceres/shared/services';
import { BehaviorSubject, Observable, of, ReplaySubject, Subject, Subscription } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

export type Result<T> = {
    result?: T;
    error?: unknown;
};

@Injectable({
    providedIn: 'root'
})
export class ExperienceService implements OnDestroy {
    private readonly httpClient = inject(ImpersonatedHttpClient);

    private readonly experiences$$: Subject<Experience[]> = new ReplaySubject(1);
    public readonly experiences$: Observable<Experience[]> = this.experiences$$.asObservable();

    private readonly experiencesLoading$$: Subject<boolean> = new BehaviorSubject(false);
    public readonly experiencesLoading$ = this.experiencesLoading$$.asObservable();

    private readonly subscription = new Subscription();

    ngOnDestroy() {
        this.subscription.unsubscribe();
    }

    public getAll() {
        this.experiencesLoading$$.next(true);
        this.subscription.add(
            this.httpClient
                .get<Experience[]>(`${environment.edgeService}/experiences`)
                .pipe(catchError(() => of([])))
                .subscribe((experiences) => {
                    this.experiences$$.next(experiences);
                    this.experiencesLoading$$.next(false);
                })
        );
    }

    public getAllExperienceNames(): Observable<string[]> {
        return this.httpClient.get<string[]>(`${environment.edgeService}/experiences/names`);
    }

    public getSelectableExperiences(usedExperienceIds: number[]): Observable<Experience[]> {
        return this.experiences$.pipe(
            map((experiences) => experiences.filter((experience) => !usedExperienceIds.includes(experience.id)))
        );
    }

    private handleAction<T>(
        actionFn: () => Observable<T>,
        resultSubject$$: Subject<T>,
        loadingSubject$$: Subject<boolean>
    ): Observable<Result<T>> {
        return of(null).pipe(
            tap(() => loadingSubject$$.next(true)),
            switchMap(actionFn),
            tap((result) => resultSubject$$.next(result)),
            map((result) => ({ result })),
            catchError((error) => of({ error })),
            tap(() => loadingSubject$$.next(false))
        );
    }

    public addExperience(experienceName: string): Observable<Result<Experience[]>> {
        return this.handleAction(
            () =>
                this.httpClient.post<Experience[]>(`${environment.edgeService}/experiences/experience`, {
                    experienceName
                }),
            this.experiences$$,
            this.experiencesLoading$$
        );
    }

    public removeExperience(experienceId: number): Observable<Result<Experience[]>> {
        return this.handleAction(
            () =>
                this.httpClient.delete<Experience[]>(
                    `${environment.edgeService}/experiences/experience/${experienceId}`
                ),
            this.experiences$$,
            this.experiencesLoading$$
        );
    }
}
